import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  Mail,
  Phone,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { BrandedHeader } from './BrandedHeader';
import { ComplianceFooter } from './ComplianceFooter';
import { SecureUploadCompanyProfileStatus } from './SecureUploadCompanyProfileStatus';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';

interface UploadSession {
  contact: {
    contactId: string;
    firstName: string;
    lastName: string;
  };
  loanOfficer: {
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phone?: string;
    nmls?: string;
    title?: string;
    photoUrl?: string;
  } | null;
  company: {
    name: string;
    email?: string;
    logoUrl?: string;
    primaryColor?: string;
  };
  verifiedAt: string;
}

interface UploadedFile {
  id: string;
  file: File;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export const BorrowerSelfServiceUpload = () => {
  const navigate = useNavigate();
  const {
    companyProfile,
    loading: profileLoading,
    error: profileError,
    retry: retryProfile
  } = useCompanyProfile();
  const [session, setSession] = useState<UploadSession | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load session from sessionStorage
  useEffect(() => {
    const sessionStr = sessionStorage.getItem('borrowerUploadSession');
    if (sessionStr) {
      try {
        const parsed = JSON.parse(sessionStr);
        // Check if session is still valid (24 hours)
        const verifiedAt = new Date(parsed.verifiedAt);
        const now = new Date();
        const hoursSinceVerification = (now.getTime() - verifiedAt.getTime()) / (1000 * 60 * 60);

        if (hoursSinceVerification > 24) {
          sessionStorage.removeItem('borrowerUploadSession');
          toast.error('Session expired. Please verify your identity again.');
          navigate('/secureuploads/borrower');
          return;
        }

        setSession(parsed);
      } catch {
        navigate('/secureuploads/borrower');
      }
    } else {
      navigate('/secureuploads/borrower');
    }
    setIsLoading(false);
  }, [navigate]);

  const uploadFile = useCallback(async (file: File, fileId: string) => {
    if (!session) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('contactId', session.contact.contactId);
    formData.append('lastName', session.contact.lastName);

    try {
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/upload-contact-document`,
        {
          method: 'POST',
          body: formData
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      setFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, status: 'success', progress: 100 } : f
      ));

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, status: 'error', error: message } : f
      ));
    }
  }, [session]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = acceptedFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: 'uploading',
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Start uploads
    newFiles.forEach(f => {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setFiles(prev => prev.map(file =>
          file.id === f.id && file.status === 'uploading'
            ? { ...file, progress: Math.min(file.progress + 10, 90) }
            : file
        ));
      }, 200);

      uploadFile(f.file, f.id).finally(() => {
        clearInterval(progressInterval);
      });
    });
  }, [uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;

  if (profileLoading || profileError || !companyProfile) {
    return (
      <SecureUploadCompanyProfileStatus
        loading={profileLoading}
        onRetry={retryProfile}
      />
    );
  }

  if (isLoading || !session) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const { contact, loanOfficer } = session;

  return (
    <div className="h-screen overflow-y-auto relative bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex flex-col">
      {/* Decorative gradient orb */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-full blur-3xl pointer-events-none animate-pulse-subtle" />

      {/* Branded Header */}
      <BrandedHeader
        title="Upload Your Documents"
        subtitle={`Hello ${contact.firstName}! Upload documents securely to your loan officer.`}
        companyProfile={companyProfile}
      />

      {/* Main Content */}
      <div className="relative flex-1 p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Loan Officer Card */}
          {loanOfficer && (
            <Card className="glass-card border border-white/60 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-cyan-200">
                    <AvatarImage src={loanOfficer.photoUrl} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-lg">
                      {loanOfficer.firstName?.[0]}{loanOfficer.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {loanOfficer.fullName}
                    </h3>
                    {loanOfficer.title && (
                      <p className="text-sm text-gray-600">{loanOfficer.title}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                      {loanOfficer.email && (
                        <a href={`mailto:${loanOfficer.email}`} className="flex items-center gap-1 hover:text-cyan-600">
                          <Mail className="w-4 h-4" />
                          {loanOfficer.email}
                        </a>
                      )}
                      {loanOfficer.phone && (
                        <a href={`tel:${loanOfficer.phone}`} className="flex items-center gap-1 hover:text-cyan-600">
                          <Phone className="w-4 h-4" />
                          {loanOfficer.phone}
                        </a>
                      )}
                      {loanOfficer.nmls && (
                        <span className="text-gray-500">NMLS# {loanOfficer.nmls}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upload Area */}
          <Card className="glass-card border border-white/60 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-cyan-600" />
                Upload Documents
              </CardTitle>
              <CardDescription>
                Drag and drop files or click to browse. Accepted formats: PDF, JPG, PNG (max 10MB each)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-300
                  ${isDragActive
                    ? 'border-cyan-500 bg-cyan-50/50 scale-[1.02]'
                    : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50/30'
                  }
                `}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-4">
                  <div className={`
                    w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300
                    ${isDragActive
                      ? 'bg-cyan-500 text-white scale-110'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}>
                    <Upload className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-700">
                      {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      or click to browse from your device
                    </p>
                  </div>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">
                      Uploaded Files ({successCount}/{files.length})
                    </h4>
                    {uploadingCount > 0 && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading {uploadingCount} file(s)...
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {files.map(file => (
                      <div
                        key={file.id}
                        className={`
                          flex items-center gap-3 p-3 rounded-lg border transition-colors
                          ${file.status === 'success'
                            ? 'bg-green-50 border-green-200'
                            : file.status === 'error'
                              ? 'bg-red-50 border-red-200'
                              : 'bg-gray-50 border-gray-200'
                          }
                        `}
                      >
                        <div className="flex-shrink-0">
                          {file.status === 'success' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : file.status === 'error' ? (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.file.name}</p>
                          {file.status === 'uploading' && (
                            <Progress value={file.progress} className="h-1 mt-1" />
                          )}
                          {file.status === 'error' && file.error && (
                            <p className="text-xs text-red-600 mt-1">{file.error}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(file.id)}
                          className="flex-shrink-0 h-8 w-8"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem('borrowerUploadSession');
                navigate('/secureuploads/borrower');
              }}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Start Over
            </Button>

            {successCount > 0 && uploadingCount === 0 && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">
                  {successCount} document{successCount !== 1 ? 's' : ''} uploaded successfully
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Footer */}
      <ComplianceFooter companyProfile={companyProfile} />
    </div>
  );
};
